import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useBot } from '../context/BotContext';
import { botApi, isBotConfigured } from '../services/botApi';

const { FiZap, FiCopy, FiArrowUpRight, FiRefreshCw, FiAlertTriangle, FiPlusCircle, FiKey } = FiIcons;

// The burner wallet card shown in LIVE mode. The key lives only on the backend;
// this just reads balance and triggers deposit-address display / withdrawals.
export default function BurnerWallet({ compact = false }) {
  const { notify } = useBot();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [dest, setDest] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [showGenConfirm, setShowGenConfirm] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [newKey, setNewKey] = useState(null); // { pubkey, secret, persisted } shown once for backup
  const [secretCopied, setSecretCopied] = useState(false);

  const refresh = useCallback(async () => {
    if (!isBotConfigured()) { setLoading(false); return; }
    try {
      const w = await botApi.getWallet();
      setWallet(w);
    } catch (_e) { /* leave as-is */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 15000);
    return () => clearInterval(id);
  }, [refresh]);

  const copy = () => {
    if (!wallet?.pubkey) return;
    navigator.clipboard.writeText(wallet.pubkey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const short = wallet?.pubkey ? `${wallet.pubkey.slice(0, 4)}...${wallet.pubkey.slice(-4)}` : '—';
  const hasWallet = Boolean(wallet?.pubkey);

  const doGenerate = async () => {
    setGenBusy(true);
    try {
      const r = await botApi.generateWallet();
      setNewKey(r);
      setShowGenConfirm(false);
      notify('New burner wallet generated — back up the secret now.', 'success');
      refresh();
    } catch (e) {
      notify(`Generate failed: ${e.message}`, 'error');
    } finally { setGenBusy(false); }
  };

  const copySecret = () => {
    if (!newKey?.secret) return;
    navigator.clipboard.writeText(newKey.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 1500);
  };

  const doWithdraw = async () => {
    if (!dest.trim() || !amount || Number(amount) <= 0) return;
    setBusy(true);
    try {
      const { txid } = await botApi.withdraw({ destination: dest.trim(), amountSol: Number(amount) });
      notify(`Withdrawal sent: ${txid.slice(0, 8)}...`, 'success');
      setShowWithdraw(false); setDest(''); setAmount('');
      refresh();
    } catch (e) {
      notify(`Withdraw failed: ${e.message}`, 'error');
    } finally { setBusy(false); }
  };

  if (!isBotConfigured()) {
    return (
      <div className="bg-[#0a0a0b] border border-yellow-500/20 rounded-xl p-3 text-yellow-400/80 text-[10px] flex items-start gap-1.5">
        <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
        Backend bot not configured (set VITE_BOT_API_URL). Live trading is unavailable.
      </div>
    );
  }

  if (compact) {
    return (
      <span className="flex items-center gap-1 bg-green-500/20 border border-green-500/40 text-green-300 text-[10px] font-bold px-2 py-1.5 rounded-lg font-mono">
        <SafeIcon icon={FiZap} className="text-[10px]" />
        {loading ? '…' : `${(wallet?.sol ?? 0).toFixed(2)} SOL`}
      </span>
    );
  }

  return (
    <div className="bg-[#0a0a0b] border border-green-500/20 rounded-xl p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <SafeIcon icon={FiZap} className="text-green-400 text-xs" />
          <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Burner Wallet</span>
        </div>
        <button onClick={refresh} className="text-gray-500 hover:text-gray-300 transition-colors">
          <SafeIcon icon={FiRefreshCw} className={`text-xs ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-green-400 font-mono font-bold text-base leading-tight">
        {loading ? '—' : (wallet?.sol ?? 0).toFixed(3)} <span className="text-xs font-normal text-green-500/70">SOL</span>
      </p>
      <button onClick={copy} className="flex items-center gap-1.5 text-[10px] text-gray-400 hover:text-white font-mono transition-colors">
        {short} <SafeIcon icon={FiCopy} className="text-[10px]" /> {copied && <span className="text-green-400">copied</span>}
      </button>
      <p className="text-[9px] text-gray-600 leading-snug">Deposit SOL to this address to fund live trading.</p>

      {!showWithdraw ? (
        <button
          onClick={() => setShowWithdraw(true)}
          className="w-full flex items-center justify-center gap-1.5 text-[10px] text-gray-300 hover:text-white bg-[#1c1c1d] hover:bg-[#2c2c2e] border border-[#2c2c2e] py-1.5 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiArrowUpRight} className="text-[10px]" /> Withdraw
        </button>
      ) : (
        <div className="space-y-1.5">
          <input
            value={dest} onChange={(e) => setDest(e.target.value)}
            placeholder="Destination address"
            className="w-full bg-[#1c1c1d] border border-[#2c2c2e] rounded-lg px-2 py-1.5 text-white text-[10px] font-mono focus:outline-none focus:border-green-500"
          />
          <div className="flex gap-1.5">
            <input
              type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="SOL"
              className="flex-1 bg-[#1c1c1d] border border-[#2c2c2e] rounded-lg px-2 py-1.5 text-white text-[10px] font-mono focus:outline-none focus:border-green-500"
            />
            <button onClick={doWithdraw} disabled={busy}
              className="px-2.5 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-[10px] font-bold transition-colors">
              {busy ? '…' : 'Send'}
            </button>
            <button onClick={() => setShowWithdraw(false)}
              className="px-2 py-1.5 rounded-lg bg-[#2c2c2e] text-gray-400 text-[10px] transition-colors">✕</button>
          </div>
        </div>
      )}

      {/* No wallet yet → prompt to generate one */}
      {!loading && !hasWallet && (
        <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-2 text-[10px] text-yellow-400/90 flex items-start gap-1.5">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
          No burner wallet yet. Generate one to start live trading.
        </div>
      )}

      {/* Manual wallet generation — destructive, hidden behind a confirm */}
      {!showGenConfirm && !newKey && (
        <button
          onClick={() => setShowGenConfirm(true)}
          className="w-full flex items-center justify-center gap-1.5 text-[10px] text-gray-400 hover:text-white bg-[#1c1c1d] hover:bg-[#2c2c2e] border border-[#2c2c2e] py-1.5 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiPlusCircle} className="text-[10px]" /> {hasWallet ? 'Generate new wallet' : 'Generate wallet'}
        </button>
      )}

      {showGenConfirm && (
        <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-2.5 space-y-2">
          <p className="text-[10px] text-red-400/90 leading-snug flex items-start gap-1.5">
            <SafeIcon icon={FiAlertTriangle} className="mt-0.5 flex-shrink-0" />
            {hasWallet
              ? 'This replaces the current wallet. Funds in the old wallet stay accessible ONLY via its old key — withdraw first or back it up.'
              : 'Generates a fresh burner wallet on the backend.'}
          </p>
          <div className="flex gap-1.5">
            <button onClick={doGenerate} disabled={genBusy}
              className="flex-[2] px-2 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[10px] font-bold transition-colors">
              {genBusy ? '…' : (hasWallet ? 'Replace wallet' : 'Generate')}
            </button>
            <button onClick={() => setShowGenConfirm(false)}
              className="px-2 py-1.5 rounded-lg bg-[#2c2c2e] text-gray-400 text-[10px] transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* One-time secret display for backup */}
      {newKey && (
        <div className="bg-[#1c1c1d] border border-green-500/30 rounded-lg p-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-green-400 font-semibold">
            <SafeIcon icon={FiKey} className="text-[10px]" /> New wallet — save this secret now
          </div>
          <p className="text-[9px] text-gray-500 font-mono break-all">{newKey.pubkey}</p>
          <p className="text-[9px] text-gray-300 font-mono break-all bg-[#0a0a0b] border border-[#2c2c2e] rounded p-1.5 select-all">
            {newKey.secret}
          </p>
          {!newKey.persisted && (
            <p className="text-[9px] text-yellow-400/90 leading-snug">
              Not saved on the server (ephemeral filesystem). Paste this into your runtime env (BURNER_PRIVATE_KEY) or it is lost on restart.
            </p>
          )}
          <div className="flex gap-1.5">
            <button onClick={copySecret}
              className="flex-[2] flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-[10px] font-bold transition-colors">
              <SafeIcon icon={FiCopy} className="text-[10px]" /> {secretCopied ? 'Copied' : 'Copy secret'}
            </button>
            <button onClick={() => setNewKey(null)}
              className="px-2 py-1.5 rounded-lg bg-[#2c2c2e] text-gray-400 text-[10px] transition-colors">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
